/**
 * feedbackSubmission — 診斷資料 → BE `feedback` 契約的映射(R251)
 * =============================================================================
 *
 * 對接 `creative-test-v2/docs/94-frontend-feedback-report-api.md`。BE 主導 wire,這一層
 * 負責把我們的診斷模型攤平成它要的表單欄位。
 *
 * ## 三個必須在這裡解決的落差
 *
 * ### ① BE 的 `title` / `description` 是**必填**,而 owner 要的是「敘述選填」
 *
 * Bryan 的原始需求是「前端只要點按鈕即可,敘述選填(通常應該只有內部願意填寫)」,
 * 但 BE 契約 `title`(1–120)與 `description`(1–4000)都必填。兩者不衝突 ——
 * **由 FE 生成**即可:`description` 本來就會帶診斷碼全文(絕不會是空的),`title` 從
 * 使用者敘述 / 最近的錯誤 / 分類 + 路由依序退讓({@link deriveTitle})。
 * 於是「一顆按鈕就送得出去」成立,而 BE 拿到的是有內容的兩個欄位。
 *
 * ### ② 診斷資料在 BE 沒有結構化欄位
 *
 * BE 沒有 `api_trail` / `console_trail` / `annotations` 欄位(owner 拍板「簡單為主」)。
 * 所以那些**全部進 `description`** —— 它允許 4000 字元且明文說「可以含 HTML 片段 /
 * 錯誤訊息」,而我們的診斷碼正是一段人與 AI 都讀得懂的文字。
 * ⚠️ 代價要說清楚:後端**無法** group by 查「哪一類端點最常出錯」,那要等 BE 願意收
 * 結構化欄位(docs/54 §S 已記)。
 *
 * ### ③ 🔴 `title` 不可含 `<` 或 `>`(BE 會回 422)
 *
 * 這條很容易踩:自動生成的標題可能來自 console stack,而 stack 裡常有 `<anonymous>`
 * 這種東西。{@link deriveTitle} 一律剝掉角括號 —— 少了這一步,「錯誤最多的那種回報」
 * 會剛好是送不出去的那種。
 */

import type { DiagnosticsReport, DiagnosticsShot } from './diagnosticsReport';

/**
 * BE 定義的七個分類(docs/94 §2.2)。
 *
 * 真正的定義在 [diagnosticsReport](./diagnosticsReport.ts)(依賴方向使然,見那邊的註解);
 * 這裡只 re-export,讓「對接 BE 契約」的讀者在這一支就找得到。**不要在這裡另立一份**。
 */
export { REPORT_CATEGORIES as FEEDBACK_CATEGORIES } from './diagnosticsReport';
export type { ReportCategory as FeedbackCategory } from './diagnosticsReport';

export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 4000;
/** BE 上限:≤3 張、單張 ≤5 MiB、整包 body ≤8 MiB(docs/94 §2.1)。FE 先擋省一趟失敗請求。 */
export const ATTACHMENT_MAX_COUNT = 3;
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const REQUEST_MAX_BYTES = 8 * 1024 * 1024;

/** 🔴 BE 對 `title` 拒收角括號 → 一律剝掉(理由見檔頭 ③)。 */
function stripAngles(s: string): string {
  return s.replace(/[<>]/g, '');
}

function firstLine(s: string): string {
  return (s.split('\n')[0] ?? '').trim();
}

function clamp(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

/**
 * 標題:使用者敘述 → 最近的失敗請求 → 未處理錯誤 → 分類 + 畫面。
 *
 * 逐級退讓而不是「沒填就寫『使用者未填寫』」:標題是後台清單上唯一看得到的一行,
 * 一百筆都叫「未填寫」等於沒有標題。用「哪個端點回了什麼錯誤碼」當標題,
 * triage 的人一眼就能分堆。
 */
export function deriveTitle(report: DiagnosticsReport, note: string): string {
  // R413.1 — 每一級都「先剝角括號、再判空」:BE 標題必填 ≥1 字,第一行只有 `<>` 的話
  // 舊寫法會送出空字串 → 整筆回報吃 422(回報功能自己送不出去)。剝完是空的就往下一級退。
  const usable = (s: string | null | undefined): string | null => {
    const v = stripAngles(s ?? '').trim();
    return v ? clamp(v, TITLE_MAX) : null;
  };

  const typed = usable(firstLine(note));
  if (typed) return typed;

  const failed = [...report.apiTrail].reverse().find((e) => e.errorCode !== null);
  const fromFailed = failed
    ? usable(`${failed.errorCode} — ${failed.method} ${failed.path}`)
    : null;
  if (fromFailed) return fromFailed;

  const crash = report.consoleTrail[report.consoleTrail.length - 1];
  const fromCrash = crash ? usable(crash.message) : null;
  if (fromCrash) return fromCrash;

  return clamp(stripAngles(`${report.category ?? 'other'} — ${report.context.route}`), TITLE_MAX);
}

/**
 * 內文 —— **只放使用者打的字**(R254 對齊 docs/94 §1)。
 *
 * 診斷資料改走 `diagnostics_b64` 專用欄位({@link diagnosticsPayload}),不再塞進這裡。
 * BE 的理由有兩層:①4000 字元裝不下整份 dump ②`description` 的內容會被 WAF 掃,
 * 而診斷全是路徑與 console 原文,`route` 這種字眼直接命中 RCE 規則(docs/94 §7)。
 *
 * 使用者有打字 → **原樣送**(他輸入「Ａ」就送「Ａ」,不加任何前後綴)。
 *
 * ⚠️ 沒打字時仍要有內容 —— BE 這欄**必填 1–4000**,而 owner 要的是「敘述選填」。
 * 退讓成一句話摘要(不是 dump):triage 的人光看清單就能分堆,不必每筆都去解 gzip。
 */
export function buildDescription(report: DiagnosticsReport, note: string): string {
  const typed = note.trim();
  if (typed) return clamp(typed, DESCRIPTION_MAX);
  return clamp(autoDescription(report), DESCRIPTION_MAX);
}

/**
 * 使用者沒填時的一句話摘要。與 {@link deriveTitle} 同一套退讓順序,但這裡可以多給一點
 * 上下文(標題只有 120 字元)。
 *
 * ⚠️ **刻意不走 i18n**:這是送進後端資料庫、給內部 triage 看的**資料**,不是畫面文案。
 * 跟著回報者語系翻譯的話,後台清單會變成中日英混雜 —— 同一種問題長三種樣子,反而難分堆。
 * 判準與 `category` 的 wire 值不翻譯相同。使用者自己打的字當然原樣保留(那是他的話)。
 */
/* eslint-disable hai/no-hardcoded-cjk -- 送給後端的資料而非 UI 文案;刻意不翻譯,理由見上方 JSDoc */
function autoDescription(report: DiagnosticsReport): string {
  const lines: string[] = ['(使用者未填寫說明)'];
  const failed = [...report.apiTrail].reverse().find((e) => e.errorCode !== null);
  if (failed) {
    lines.push(
      `最近的失敗請求:${failed.method} ${failed.path} → ${failed.status ?? 'ERR'} ${failed.errorCode}`,
    );
  }
  const crash = report.consoleTrail[report.consoleTrail.length - 1];
  if (crash) lines.push(`未處理錯誤:${crash.message}`);
  lines.push(`畫面:${report.context.route}`);
  const circled = report.shots.reduce((n, e) => n + e.annotations.length, 0);
  if (circled > 0) {
    lines.push(`使用者圈了 ${circled} 處(見附圖紅框)`);
  }
  lines.push('完整診斷在 diagnostics_b64。');
  return lines.join('\n');
}
/* eslint-enable hai/no-hardcoded-cjk */

/**
 * 要放進 `diagnostics_b64` 的物件(docs/94 §6:`apiTrail` / `consoleTrail` /
 * `annotations` / `client` / `context` 整包原樣送)。
 *
 * ⚠️ **剝掉 `shot.dataBase64`**:截圖本體走 `files` 檔案 part,診斷裡再放一份 base64 是
 * 重複,而且會直接吃掉那 64 KiB 的編碼後上限(一張 1280 寬的截圖就 200–400 KB)。
 * 只留尺寸摘要,讓 admin 端知道「有附圖、多大」。
 *
 * ⚠️ **不要往這裡加 request/response body 或 header dump**(docs/94 §6 明文要求):
 * 後端的去敏是縱深防禦不是免責 —— 它抹不掉的形狀會原封不動存進資料庫並顯示在 admin
 * 介面上。我們這側本來就只記形狀(見 diagnosticsBuffer 檔頭),維持那條線。
 */
export function diagnosticsPayload(report: DiagnosticsReport): Record<string, unknown> {
  const { shots, ...rest } = report;
  return {
    ...rest,
    // 每張只留尺寸摘要 + 它自己的框(base64 走 `files`;`shots` 的順序 = files 的順序,
    // admin 端才對得回「第 2 張圖上的那個框」)。
    shots: shots.map((e) => ({
      mime: e.shot.mime,
      width: e.shot.width,
      height: e.shot.height,
      bytes: e.shot.bytes,
      annotations: e.annotations,
    })),
  };
}

/**
 * gzip + base64(docs/94 §6 的編碼)。
 *
 * **為什麼要編碼**:不是省流量,是讓 Cloud Armor 讀不到內容 —— 診斷全是路徑、UA、console
 * 原文,`route` 這個字就命中 CRS 的 RCE 規則(932100)。編碼後整類誤判消失,順便小一半。
 *
 * ⚠️ `CompressionStream` 不是所有環境都有(Safari 16.4 前、jsdom 沒有)→ 退回**未壓縮的
 * base64**,BE 明文「同一把尺、也接受」。所以這支永遠給得出結果,不需要呼叫端處理失敗。
 */
export async function gzipBase64(obj: unknown): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(obj));
  const bytes = await tryGzip(json);
  return base64OfBytes(bytes);
}

async function tryGzip(input: Uint8Array): Promise<Uint8Array> {
  const Comp = (globalThis as { CompressionStream?: typeof CompressionStream }).CompressionStream;
  if (typeof Comp !== 'function') return input;
  try {
    const stream = new Blob([input as BlobPart]).stream().pipeThrough(new Comp('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    // 壓縮失敗不該讓整筆回報送不出去 —— 未壓縮的 base64 BE 也收。
    return input;
  }
}

function base64OfBytes(bytes: Uint8Array): string {
  // ⚠️ 逐 chunk 轉字串而不是 `btoa(String.fromCharCode(...bytes))`:後者用展開運算子把
  // 每個 byte 當成一個引數,大 payload 直接撞 call stack 上限。
  //
  // **實測(Chrome 148)**:237 KB 的 gzip bytes → 本函式跨 30 個 chunk 正常編碼、
  // 解回來逐字相同;同一份資料餵給展開運算子當場
  // `RangeError: Maximum call stack size exceeded`。不是理論上的擔心。
  let bin = '';
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** BE 對編碼後字串的上限(docs/94 §6);超過回 422 `FEEDBACK_DIAGNOSTICS_INVALID`。 */
export const DIAGNOSTICS_B64_MAX_BYTES = 64 * 1024;

/**
 * `DiagnosticsShot`(mime + base64)→ `File`(BE 的 `files` 欄位要真的檔案)。
 *
 * 🔴 **參數刻意是 `DiagnosticsShot` 而不是 `CapturedShot`**:送出路徑手上有兩份圖 ——
 * 原始截圖(`CapturedShot`)與**燒好標註框**的那份(`renderAnnotatedShot` 的輸出,
 * 型別是 `DiagnosticsShot`)。附給 BE 的必須是後者,否則使用者圈的框在圖片上不見了,
 * 「打開圖片就看到紅框」這個價值整個丟失(座標雖仍在 description,但那是給程式讀的)。
 * 用型別把這件事釘死:傳錯那份會是**編譯錯誤**,不是線上才發現的靜默降級。
 */
export function shotToFile(shot: DiagnosticsShot, index = 1): File | null {
  const mime = shot.mime || 'image/webp';
  try {
    const bin = atob(shot.dataBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const ext = mime === 'image/png' ? 'png' : mime === 'image/jpeg' ? 'jpg' : 'webp';
    // 檔名只留 BE 允許的字元集(`[A-Za-z0-9._- ]`),避免它幫我們換成底線。
    return new File([bytes], `screenshot-${index}.${ext}`, { type: mime });
  } catch {
    return null;
  }
}

/** 送出前的本地檢查(BE 也會擋,兩邊都要 —— docs/94 §6 驗收項 6)。 */
export function attachmentsWithinLimits(files: readonly File[]): boolean {
  if (files.length > ATTACHMENT_MAX_COUNT) return false;
  if (files.some((f) => f.size > ATTACHMENT_MAX_BYTES)) return false;
  return files.reduce((sum, f) => sum + f.size, 0) <= REQUEST_MAX_BYTES;
}

/**
 * 送出失敗 → 該對使用者說什麼(docs/94 §2.7 十三個錯誤碼的收斂)。
 *
 * 判準是**「重試有沒有用」**,不是「錯誤碼長什麼樣」:
 *
 * - 限流 / 每日上限 → 等一下再送就會成功 → 叫他稍後再試,**不要**自動複製診斷碼
 *   (那會暗示「送不出去了,自己貼吧」,而其實只要等一分鐘)。
 * - 其他(驗證失敗、系統忙碌、建立失敗)→ 使用者自己無能為力 → 走「已幫你複製診斷碼」
 *   那條路,讓他手上有東西可以貼給我們。
 *
 * ⚠️ 圖片相關的六個 413/400 目前碰不到(截圖檔案由 WAF gate 關著),但仍歸在 retryable=false
 * 這一類 —— 那些是「這張圖不行」,重送同一張沒有用。
 */
export interface SubmitFailure {
  /** 稍後重試就可能成功。 */
  retryable: boolean;
  /** i18n key 的尾段(`report.submitError.*`)。 */
  reason: 'rateLimited' | 'dailyLimit' | 'busy' | 'invalid' | 'generic';
}

export function describeSubmitFailure(status: number, code: string | null): SubmitFailure {
  if (code === 'FEEDBACK_DAILY_LIMIT_EXCEEDED') return { retryable: true, reason: 'dailyLimit' };
  // slowapi 的 429 `error.code` 是**數字**(docs/87 B-3 同型),所以靠 status 兜底。
  if (status === 429) return { retryable: true, reason: 'rateLimited' };
  if (status === 503 || code === 'FEEDBACK_BUSY') return { retryable: true, reason: 'busy' };
  if (status === 422 || status === 413) return { retryable: false, reason: 'invalid' };
  return { retryable: false, reason: 'generic' };
}
